import React, { useState } from "react";
import { getImageUrl } from "helpers/Image.helper";

interface ImageSlidePopupProps {
    onClose: () => void;
    imageInfoList: any[];
    initialSlideIdx?: number;
}

const ImageSlidePopup: React.FC<ImageSlidePopupProps> = (props) => {
    const {
        onClose,
        imageInfoList,
        initialSlideIdx = 0
    } = props;

    const [slideIdx, setSlideIdx] = useState(initialSlideIdx);

    // 키보드 이벤트 처리
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowLeft':
                    if (slideIdx > 0) {
                        setSlideIdx(slideIdx - 1);
                    }
                    break;
                case 'ArrowRight':
                    if (slideIdx < imageInfoList.length - 1) {
                        setSlideIdx(slideIdx + 1);
                    }
                    break;
                case 'Escape':
                    onClose();
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [slideIdx, imageInfoList.length, onClose]);

    // 터치/스와이프 이벤트 처리
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && slideIdx < imageInfoList.length - 1) {
            setSlideIdx(slideIdx + 1);
        } else if (isRightSwipe && slideIdx > 0) {
            setSlideIdx(slideIdx - 1);
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
            <div className="relative w-full max-w-6xl h-full p-4">
                {/* 닫기 버튼 */}
                <button
                    className="absolute top-6 right-6 z-20 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
                    onClick={onClose}
                    aria-label="닫기"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* 이미지 슬라이더 */}
                <div className="relative w-full h-full">
                    <div 
                        className="w-full h-full flex items-center justify-center overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div 
                            className="flex w-full h-full transition-transform duration-300 ease-in-out" 
                            style={{ transform: `translateX(-${slideIdx * 100}%)` }}
                        >
                            {imageInfoList.map((image: any, index: number) => (
                                <div key={index} className="w-full h-full flex-shrink-0 flex items-center justify-center p-4">
                                    <img
                                        src={getImageUrl(image)}
                                        alt={`이미지 ${index + 1}`}
                                        className="max-w-full max-h-full object-contain rounded-lg"
                                        draggable={false}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 이전/다음 버튼 */}
                    {slideIdx > 0 && (
                        <button
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70"
                            onClick={() => setSlideIdx(slideIdx - 1)}
                            aria-label="이전 이미지"
                        >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    )}
                    {slideIdx < imageInfoList.length - 1 && (
                        <button
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70"
                            onClick={() => setSlideIdx(slideIdx + 1)}
                            aria-label="다음 이미지"
                        >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}

                    {/* 이미지 인디케이터 */}
                    {imageInfoList.length > 1 && (
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
                            {imageInfoList.map((_, index) => (
                                <button
                                    key={index}
                                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                        index === slideIdx 
                                            ? 'bg-white scale-125' 
                                            : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                                    }`}
                                    onClick={() => setSlideIdx(index)}
                                    aria-label={`이미지 ${index + 1}로 이동`}
                                />
                            ))}
                        </div>
                    )}

                    {/* 이미지 카운터 */}
                    <div className="absolute top-6 left-6 text-white bg-black bg-opacity-50 rounded-full px-3 py-1 text-sm font-medium">
                        {slideIdx + 1} / {imageInfoList.length}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageSlidePopup;
